const CHANNEL_CODE_KEY = 'ssg_channel_code';
const CHANNEL_CODE_DOC_KEY = 'channelCode';

function readChannelCodeFromUrl(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('channelCode')
      || params.get('channelcode')
      || Array.from(params.entries()).find(([key]) => key.toLowerCase() === 'channelcode')?.[1]
      || '';
  } catch {
    return '';
  }
}

export function initChannelCode(): void {
  const code = readChannelCodeFromUrl();
  if (!code) return;

  try {
    window.sessionStorage.setItem(CHANNEL_CODE_KEY, code);
    window.sessionStorage.setItem(CHANNEL_CODE_DOC_KEY, code);
  } catch {
    // ignore – sessionStorage may be unavailable in some environments
  }
}

export function getChannelCode(): string {
  const fromUrl = readChannelCodeFromUrl();
  if (fromUrl) return fromUrl;

  try {
    return window.sessionStorage.getItem(CHANNEL_CODE_KEY)
      ?? window.sessionStorage.getItem(CHANNEL_CODE_DOC_KEY)
      ?? '';
  } catch {
    return '';
  }
}

interface NativeEventPayload {
  eventName: string;
  params: Record<string, unknown>;
}

function callNativeEvent(handlerName: string, payload: NativeEventPayload): void {
  const w = window as any;
  const json = JSON.stringify(payload);

  try {
    w.webkit?.messageHandlers?.[handlerName]?.postMessage(payload);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[nativeBridge] iOS', err);
  }

  const bridge = w.jsBridge;
  if (!bridge || typeof bridge.postMessage !== 'function') {
    return;
  }

  try {
    bridge.postMessage(handlerName, json);
  } catch {
    try {
      bridge.postMessage(json);
    } catch (err2) {
      // eslint-disable-next-line no-console
      console.warn('[nativeBridge] jsBridge', err2);
    }
  }
}

export function callEventRegisterNative({ channelCode, uid }: { channelCode: string; uid: string }): void {
  callNativeEvent('eventRegister', {
    eventName: 'register',
    params: {
      channelCode: String(channelCode || ''),
      uid: String(uid || ''),
      timestamp: String(Date.now()),
    },
  });
}

export function callEventMessageSentNative({ channelCode, uid }: { channelCode: string; uid: string }): void {
  callNativeEvent('eventMessageSent', {
    eventName: 'messageSent',
    params: {
      channelCode: String(channelCode || ''),
      uid: String(uid || ''),
      timestamp: String(Date.now()),
    },
  });
}

let lastLoginUid: string | undefined;

export function callEventLoginNative({ channelCode, uid }: { channelCode: string; uid: string }): void {
  const uidStr = String(uid || '');
  if (!uidStr || uidStr === lastLoginUid) return;
  lastLoginUid = uidStr;

  callNativeEvent('eventLogin', {
    eventName: 'login',
    params: {
      channelCode: String(channelCode || ''),
      uid: uidStr,
      timestamp: String(Date.now()),
    },
  });
}

export function callEventLogoutNative({ uid }: { uid?: string }): void {
  lastLoginUid = undefined;

  callNativeEvent('eventLogout', {
    eventName: 'logout',
    params: {
      uid: String(uid || ''),
      timestamp: String(Date.now()),
    },
  });
}

export function callEventWebAliveNative(): void {
  callNativeEvent('eventWebAlive', {
    eventName: 'webAlive',
    params: {
      timestamp: String(Date.now()),
    },
  });
}

export function callEventWebReadyNative(): void {
  callNativeEvent('eventWebReady', {
    eventName: 'webReady',
    params: {
      timestamp: String(Date.now()),
    },
  });
}

export function callEventChatOpenNative(chatId: string): void {
  callNativeEvent('eventChatOpen', {
    eventName: 'chatOpen',
    params: {
      chatId: String(chatId),
      timestamp: String(Date.now()),
    },
  });
}

export function callEventChatCloseNative(chatId: string): void {
  callNativeEvent('eventChatClose', {
    eventName: 'chatClose',
    params: {
      chatId: String(chatId),
      timestamp: String(Date.now()),
    },
  });
}

export function callEventChatReadNative(chatId: string): void {
  callNativeEvent('eventChatRead', {
    eventName: 'chatRead',
    params: {
      chatId: String(chatId),
      timestamp: String(Date.now()),
    },
  });
}

export function callEventUnreadCountNative(count: number): void {
  callNativeEvent('eventUnreadCount', {
    eventName: 'unreadCount',
    params: {
      count: String(Math.max(0, Math.floor(count || 0))),
      timestamp: String(Date.now()),
    },
  });
}
